import React from "react";
import { Button } from "../ui/button";
import { useToast } from "../ui/use-toast";
import { Shuffle, Undo } from "lucide-react";
import {
    isGuessCorrect,
    isGuessRepeated,
    shuffleGameData,
} from "../../lib/game-helpers";

import { GameStatusContext } from "../../providers/GameStatus";
import { PuzzleDataContext } from "../../providers/PuzzleData";

function GameControlButtonsPanel({
    shuffledRows,
    setShuffledRows,
    setGridShake,
}) {
    const {
        isGameOver,
        guessCandidate,
        setGuessCandidate,
        submittedGuesses,
        setSubmittedGuesses,
        solvedGameData,
        setSolvedGameData,
    } = React.useContext(GameStatusContext);
    const { gameData } = React.useContext(PuzzleDataContext);
    const { toast } = useToast();

    function deselectAll() {
        setGuessCandidate([]);
    }

    function submitCandidateGuess() {
        // check that its a valid guess by size
        if (guessCandidate.length < 2) {
            return;
        }
        // check that the guess hasnt already been submitted previously
        if (isGuessRepeated({ submittedGuesses, guessCandidate })) {
            toast({
                title: "Already guessed",
                description: "You've tried this combination before.",
                className: "bg-slate-800/90 text-white border-slate-700 backdrop-blur-sm",
            });

            return;
        }
        // add guess to state
        setSubmittedGuesses([...submittedGuesses, guessCandidate]);
        // check if the guess is correct
        const {
            isCorrect,
            correctWords,
            correctCategory,
            isGuessOneAway,
            isGuessTwoAway,
            correctDifficulty,
        } = isGuessCorrect({
            guessCandidate,
            gameData,
        });

        // if the guess is correct:
        // set it as solved in game data
        if (isCorrect) {
            setSolvedGameData([
                ...solvedGameData,
                {
                    category: correctCategory,
                    words: correctWords,
                    difficulty: correctDifficulty,
                },
            ]);
            setGuessCandidate([]);
        } else {
            // Shake the grid to give feedback that they were wrong
            setGridShake(true);
            if (isGuessOneAway) {
                toast({
                    title: "One away!",
                    description: "So close - just one tile off.",
                    className: "bg-amber-500/90 text-white border-amber-600 backdrop-blur-sm",
                });
            } else if (isGuessTwoAway) {
                toast({
                    title: "Two away!",
                    description: "Getting warmer - two tiles off.",
                    className: "bg-slate-700/90 text-white border-slate-600 backdrop-blur-sm",
                });
            }
        }
    }

    return (
        <div className="grid grid-cols-3 gap-2 sm:gap-3 md:gap-4 mt-2 sm:mt-3">
            <Button
                disabled={isGameOver}
                variant="outline"
                size="default"
                className="h-10 sm:h-12 md:h-12 border-slate-600 text-slate-200 hover:bg-slate-700 hover:text-white bg-transparent"
                onClick={() =>
                    setShuffledRows(shuffleGameData({ gameData: shuffledRows }))
                }
            >
                <Shuffle className="h-4 w-4 mr-2" strokeWidth={2} />
                <span className="text-xs sm:text-sm font-medium uppercase tracking-wider">Shuffle</span>
            </Button>
            <Button
                disabled={isGameOver}
                variant="outline"
                size="default"
                className="h-10 sm:h-12 md:h-12 border-slate-600 text-slate-200 hover:bg-slate-700 hover:text-white bg-transparent"
                onClick={deselectAll}
            >
                <Undo className="h-4 w-4 mr-2" strokeWidth={2} />
                <span className="text-xs sm:text-sm font-medium uppercase tracking-wider">Deselect</span>
            </Button>
            <Button
                variant="submit"
                size="default"
                className="h-10 sm:h-12 md:h-12"
                onClick={submitCandidateGuess}
                disabled={isGameOver || guessCandidate.length < 2}
            >
                <span className="text-sm sm:text-base font-bold uppercase tracking-wider">Submit</span>
            </Button>
        </div>
    );
}

export default GameControlButtonsPanel;